import { useEffect, useState } from "react";
import { useWorkspace } from "../app/workspace";
import { usd, shortDate, relDate } from "../lib/format";
import { Icon } from "../components/icons";
import { Avatar, Empty } from "../components/ui";
import type { QbrDeck, ForecastCategory } from "../lib/types";
import * as work from "../lib/workClient";

const CAT_LABEL: Record<ForecastCategory, string> = { commit: "Commit", likely: "Likely", at_risk: "At risk", churn: "Churn" };
const bandColor = (b: string) => (b === "good" ? "var(--good)" : b === "watch" ? "var(--watch)" : "var(--risk)");
const dirIcon = (d: string) => (d === "up" ? "arrowUp" : d === "down" ? "arrowDown" : "dot");
const dirColor = (d: string) => (d === "up" ? "var(--good)" : d === "down" ? "var(--risk)" : "var(--faint)");

export default function Qbr() {
  const { ws } = useWorkspace();
  const [mode, setMode] = useState<work.Mode>("local");
  const accounts = [...ws.accounts].sort((a, b) => a.name.localeCompare(b.name));
  const [accountId, setAccountId] = useState(accounts[0]?.id ?? "");
  const [deck, setDeck] = useState<QbrDeck | undefined>(undefined);

  useEffect(() => { if (accountId) work.loadQbr(ws, accountId).then((r) => { setMode(r.mode); setDeck(r.deck); }); }, [ws, accountId]);

  return (
    <div className="page">
      <div className="row between mb-24 qbr-hide-print" style={{ alignItems: "flex-start" }}>
        <div>
          <p className="eyebrow">Business reviews</p>
          <h1 style={{ fontFamily: "var(--serif)", fontSize: 28, marginTop: 6 }}>QBR builder</h1>
          <p className="page__intro" style={{ marginBottom: 0 }}>A quarterly business review, assembled from the account's live data in one click. Pick an account and present.</p>
        </div>
        <div className="row" style={{ gap: 10, alignItems: "center" }}>
          <ModePill mode={mode} />
          <select className="inp inp--sm" value={accountId} onChange={(e) => setAccountId(e.target.value)}>
            {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
          <button className="btn btn--sm" onClick={() => window.print()}><Icon name="doc" size={14} /> Print / PDF</button>
        </div>
      </div>

      {!deck ? <div className="card"><div className="card__bd"><Empty>Select an account to build its QBR.</Empty></div></div> : (
        <div className="qbr-deck">
          <div className="qbr-cover card">
            <div className="card__bd">
              <div className="row between" style={{ alignItems: "flex-start" }}>
                <div className="row" style={{ gap: 14 }}>
                  <Avatar name={deck.accountName} size="lg" />
                  <div>
                    <p className="eyebrow" style={{ margin: 0 }}>{deck.period} Business Review</p>
                    <h2 style={{ fontFamily: "var(--serif)", fontSize: 26, margin: "4px 0 2px" }}>{deck.accountName}</h2>
                    <span className="faint" style={{ fontSize: 13 }}>{deck.headline.lifecycleStage} · Owned by {deck.ownerName}</span>
                  </div>
                </div>
                <span className="badge" style={{ color: bandColor(deck.headline.healthBand), borderColor: "currentColor" }}>Health {deck.headline.health}{deck.headline.healthDelta ? ` (${deck.headline.healthDelta > 0 ? "+" : ""}${deck.headline.healthDelta})` : ""}</span>
              </div>
            </div>
          </div>

          <div className="stats">
            <Stat icon="target" label="Annual value" value={usd(deck.headline.arr)} foot={`Renews ${shortDate(deck.headline.renewalDate)}`} />
            <Stat icon="pulse" label="Health trajectory" value={`${deck.headline.healthDelta > 0 ? "+" : ""}${deck.headline.healthDelta}`} foot="Change this quarter" color={deck.headline.healthDelta >= 0 ? "var(--good)" : "var(--risk)"} />
            <Stat icon="user" label="Seat utilization" value={`${deck.adoption.utilization}%`} foot={`${deck.adoption.seatsActive} of ${deck.adoption.seats} active`} color={deck.adoption.utilization >= 70 ? "var(--good)" : "var(--watch)"} />
            <Stat icon="star" label="NPS" value={deck.nps.score === null ? "—" : `${deck.nps.score > 0 ? "+" : ""}${deck.nps.score}`} foot={`${deck.nps.responses} response${deck.nps.responses === 1 ? "" : "s"}`} color={deck.nps.score === null ? undefined : deck.nps.score >= 30 ? "var(--good)" : "var(--watch)"} />
          </div>

          <div className="auto-layout">
            <div style={{ display: "grid", gap: 16 }}>
              <div className="card">
                <div className="card__hd"><div><h3>Health drivers</h3><span className="sub">What's moving the score</span></div></div>
                <div className="card__bd" style={{ display: "grid", gap: 10 }}>
                  {deck.scoreFactors.map((f) => (
                    <div key={f.label} className="row between">
                      <span className="row" style={{ gap: 8, fontSize: 13 }}><Icon name={dirIcon(f.direction)} size={14} className="" /> {f.label}</span>
                      <span className="row" style={{ gap: 8 }}>
                        <span className="mini-bar"><span style={{ width: `${Math.min(100, f.value)}%`, background: dirColor(f.direction) }} /></span>
                        <b style={{ fontSize: 13, width: 34, textAlign: "right" }}>{f.value}</b>
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {deck.renewal && (
                <div className="card">
                  <div className="card__hd"><div><h3>Renewal outlook</h3><span className="sub">Commercial motion ahead</span></div></div>
                  <div className="card__bd">
                    <div className="row" style={{ gap: 24, flexWrap: "wrap" }}>
                      <KV label="Renews" value={shortDate(deck.renewal.date)} sub={relDate(deck.renewal.date)} />
                      <KV label="Contract" value={usd(deck.renewal.arr)} />
                      <KV label="Forecast" value={CAT_LABEL[deck.renewal.category]} sub={usd(deck.renewal.forecastAmount)} />
                      {deck.renewal.expansionAmount > 0 && <KV label="Expansion" value={"+" + usd(deck.renewal.expansionAmount)} />}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div style={{ display: "grid", gap: 16 }}>
              <ListCard title="Wins this quarter" icon="check" tone="good" items={deck.wins} empty="No standout wins logged yet." />
              <ListCard title="Risks & watch items" icon="bolt" tone="risk" items={deck.risks} empty="No open risks — steady quarter." />
              <ListCard title="Recommended next steps" icon="route" tone="corona" items={deck.nextSteps} empty="No open actions." />
            </div>
          </div>

          {deck.nps.lastComment && (
            <div className="card">
              <div className="card__bd">
                <p className="eyebrow" style={{ margin: "0 0 8px" }}>Voice of the customer</p>
                <blockquote className="qbr-quote">"{deck.nps.lastComment}"</blockquote>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ListCard({ title, icon, tone, items, empty }: { title: string; icon: string; tone: "good" | "risk" | "corona"; items: string[]; empty: string }) {
  const color = tone === "good" ? "var(--good)" : tone === "risk" ? "var(--risk)" : "var(--corona)";
  return (
    <div className="card">
      <div className="card__hd"><div><h3><Icon name={icon} size={15} /> {title}</h3><span className="sub">{items.length} item{items.length === 1 ? "" : "s"}</span></div></div>
      <div className="card__bd">
        {items.length === 0 ? <span className="faint" style={{ fontSize: 13 }}>{empty}</span> : (
          <ul className="qbr-list">
            {items.map((t, i) => <li key={i}><span className="qbr-dot" style={{ background: color }} />{t}</li>)}
          </ul>
        )}
      </div>
    </div>
  );
}

function KV({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div>
      <div className="labeled__l">{label}</div>
      <div style={{ fontSize: 16, fontWeight: 600, marginTop: 3 }}>{value}</div>
      {sub && <div className="faint" style={{ fontSize: 12 }}>{sub}</div>}
    </div>
  );
}
function Stat({ icon, label, value, foot, color }: { icon: string; label: string; value: string; foot: string; color?: string }) {
  return (
    <div className="stat">
      <div className="stat__label"><Icon name={icon} size={14} /> {label}</div>
      <div className="stat__value" style={{ color, fontSize: 24 }}>{value}</div>
      <div className="stat__foot">{foot}</div>
    </div>
  );
}
function ModePill({ mode }: { mode: work.Mode }) {
  return <span className={`mode-pill ${mode === "api" ? "live" : ""}`} title={mode === "api" ? "Connected to the Penumbrai API" : "Standalone demo — changes saved to this browser"}><i />{mode === "api" ? "Live API" : "Local demo"}</span>;
}
